"use client"

import { useEffect, useState } from "react"
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/molecules/AccordionItem/accordion-item"
import { cn, slugToName } from '@/utils/index'
import { getCategoriesAction } from "@/actions/product/product.action"
import { Checkbox } from "@/components/atoms/Checkbox/checkbox"

interface CategoryNode {
  id: string
  name: string
  isSubcategory: boolean
  children: CategoryNode[]
}

interface CategoryFProps {
  id: string
  title: string
  selectedCategories: string[]
  selectedSubcategories: string[]
  onCategoryChange: (categoryId: string, isSelected: boolean) => void
  onSubcategoryChange: (subcategoryId: string, isSelected: boolean) => void
}

// Cache implementation
const categoryCache = {
  data: null as CategoryNode[] | null,
  lastUpdated: 0,
  ttl: 60 * 60 * 1000, // 1 hour cache
  isValid() {
    return this.data && Date.now() - this.lastUpdated < this.ttl
  },
}

export function CategoryFacet({
  id,
  title,
  selectedCategories,
  selectedSubcategories,
  onCategoryChange,
  onSubcategoryChange,
}: CategoryFProps) {
  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchCategories = async () => {
      if (categoryCache.isValid()) {
        setCategoryTree(categoryCache.data!)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const response: any = await getCategoriesAction()
        if (!response?.data?.data) throw new Error("Invalid category data")

        // Transform the nested API response into a proper tree structure
        const buildTree = (categories: any[], parentId: string | null = null): CategoryNode[] => {
          return categories
            .filter((category) => category.parentId === parentId)
            .map((category) => ({
              id: category.id,
              name: category.name || category.translatedName || "Unnamed Category",
              isSubcategory: parentId !== null,
              children: buildTree(categories, category.id),
            }))
        }

        // First flatten the nested structure into a single array
        const flattenCategories = (categories: any[]): any[] => {
          return categories.reduce((acc, category) => {
            const children = category.subcategories || []
            return [
              ...acc,
              { ...category, subcategories: undefined }, // Remove nested subcategories
              ...flattenCategories(children),
            ]
          }, [])
        }

        const allCategories = flattenCategories(response.data.data)
        const treeData = buildTree(allCategories)

        categoryCache.data = treeData
        categoryCache.lastUpdated = Date.now()
        setCategoryTree(treeData)
      } catch (error) {
        console.error("Failed to load categories:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCategories()
  }, [])

  const handleCheckboxChange = (node: CategoryNode, isChecked: boolean) => {
    if (node.isSubcategory) {
      onSubcategoryChange(node.id, isChecked)
    } else {
      onCategoryChange(node.id, isChecked)
      // When unchecking a category, also uncheck all its subcategories
      if (!isChecked) {
        const uncheckSubcategories = (children: CategoryNode[]) => {
          children.forEach((child) => {
            if (child.isSubcategory && selectedSubcategories.includes(child.id)) {
              onSubcategoryChange(child.id, false)
            }
            uncheckSubcategories(child.children)
          })
        }
        uncheckSubcategories(node.children)
      }
    }
  }

  const renderCategoryNode = (node: CategoryNode, level = 0) => {
    const isSelected = node.isSubcategory
      ? selectedSubcategories.includes(node.id)
      : selectedCategories.includes(node.id)

    // Only show subcategories if parent is selected
    const shouldShowChildren = node.children.length > 0 && !node.isSubcategory && selectedCategories.includes(node.id)

    return (
      <div key={node.id} className="mb-1">
        <div className="flex items-center py-1">
          <Checkbox
            id={`cat-${node.id}`}
            checked={isSelected}
            onCheckedChange={(checked) => handleCheckboxChange(node, checked === true)}
          />
          <label
            htmlFor={`cat-${node.id}`}
            className={cn(
              "ml-2 cursor-pointer select-none text-sm",
              isSelected ? "font-medium text-orange-600" : "text-gray-400 lg:text-gray-700 dark:text-gray-200",
              !node.isSubcategory && "font-medium" // Make parent categories bolder
            )}
          >
            {slugToName(node.name)}
          </label>
        </div>

        {shouldShowChildren && (
          <div className={cn("ml-4 pl-4", level > 0 ? "border-l border-gray-200" : "")}>
            {node.children.map((child) => renderCategoryNode(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <AccordionItem value={id}>
      <AccordionTrigger className="p-2 text-base">{title}</AccordionTrigger>
      <AccordionContent className="pb-4 pt-2">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 w-full animate-pulse rounded bg-gray-200" />
            ))}
          </div>
        ) : categoryTree.length > 0 ? (
          <div className="max-h-[400px] overflow-y-auto">
            {categoryTree
              .filter((node) => !node.isSubcategory) // Only show top-level categories initially
              .map((node) => renderCategoryNode(node))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-200">No categories found</p>
        )}
      </AccordionContent>
    </AccordionItem>
  )
}
